import { css, Global } from '@emotion/react';
import { useState } from 'react';
import { useOutsideClick } from 'src/hooks';
import { CONTENTFUL_ENVIRONMENT, CONTENTFUL_SPACE_ID } from 'src/env';
import styled from 'threads5/styles/styled';

const devMenuGlobalStyles = css`
  [data-contentful-link-added] {
    border: tomato 3px solid;
    border-radius: 6px;
    overflow: hidden;
    margin: 10px !important;
    position: relative;
    min-width: 250px;
    min-height: 30px;
  }
  [data-contentful-link-added] a.dev-menu-contentful-link {
    background-color: tomato !important;
    color: white !important;
    text-decoration: none !important;
    padding: 0 6px 2px 6px;
    margin: 0;
    margin-left: 20px;
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 4px;
    display: inline-flex;
    position: absolute !important;
    top: 0;
    left: 0;
    right: initial;
    bottom: initial;
    z-index: 99999999;
    font-size: 1.2rem;
    font-family: monospace;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1 !important;
  }
  a.dev-menu-contentful-link:hover {
    background-color: tomato !important;
    color: rgba(255, 255, 255, 0.75) !important;
  }
`;

const StyledDevDot = styled('div')({
  position: 'fixed',
  top: '15px',
  left: '15px',
  width: '15px',
  height: '15px',
  backgroundColor: 'white',
  mixBlendMode: 'difference',
  opacity: 0.2,
  borderRadius: '50%',
  zIndex: 99999999,
  cursor: 'pointer',
});

const StyledDevMenu = styled('div')({
  position: 'fixed',
  top: '5px',
  left: '5px',
  backgroundColor: 'white',
  zIndex: 999999999,
  display: 'flex',
  flexDirection: 'column',
  padding: '10px',
  borderRadius: '10px',
  boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
  gap: '10px',
});

const StyledCloseButton = styled('button')({
  alignSelf: 'flex-start',
});

const DevMenu = () => {
  const [isDevMenuOpen, setIsDevMenuOpen] = useState(false);

  const openMenu = () => {
    setIsDevMenuOpen(true);
  };

  const closeMenu = () => {
    setIsDevMenuOpen(false);
  };

  const devMenuRef = useOutsideClick<HTMLDivElement>(closeMenu);

  const inspectContentfulComponents = () => {
    // add link to every contentful element and the page itself, exclude those with a link already added
    // data-contentful-entry-id is added automatically by useContentfulInspectorMode
    // data-contentful-page-id is manually added to page templates
    const elements = Array.from(
      document.querySelectorAll(
        '[data-contentful-entry-id]:not([data-contentful-link-added])',
      ),
    );
    const page = Array.from(
      document.querySelectorAll(
        '[data-contentful-page-id]:not([data-contentful-link-added])',
      ),
    );
    const contentfulComponents = [...elements, ...page];

    if (contentfulComponents.length === 0) {
      alert('No components found.\nPage likely not backed by Contentful.');
    }

    contentfulComponents.forEach((component) => {
      const elementId =
        component.getAttribute('data-contentful-entry-id') ||
        component.getAttribute('data-contentful-page-id');
      const elementUrl = `https://app.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}/entries/${elementId}`;

      const link = document.createElement('a');
      link.href = elementUrl;
      link.textContent = elementId.slice(0, 8);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'dev-menu-contentful-link';

      component.prepend(link);
      component.setAttribute('data-contentful-link-added', 'true');
    });

    setIsDevMenuOpen(false);
  };

  return (
    <>
      <Global styles={devMenuGlobalStyles} />
      <StyledDevDot onClick={openMenu} data-testid='dev-menu-dot' />

      {isDevMenuOpen && (
        <StyledDevMenu ref={devMenuRef}>
          <StyledCloseButton onClick={closeMenu}>&times;</StyledCloseButton>
          <div>
            <h5>Dev Menu</h5>
            <div>
              <button type='button' onClick={inspectContentfulComponents}>
                Inspect Contentful Components
              </button>
            </div>
          </div>
        </StyledDevMenu>
      )}
    </>
  );
};

export default DevMenu;
